import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import cn from 'classnames'
import { Tooltip } from 'react-tooltip'

import styles from './styles.module.scss'
import { getPlaceAttendance } from '../../../api'

import DateSelector from '../../Core/DateSelector'
import { RateIcon, ClockIcon, CrossIcon, CupIcon, AttentionIcon, ArrowIcon, AttentionIcon2 } from './icons'
import ClockoutModal from '../ClockoutModal'

const statusMap = {
  working: {
    border: '#BEF3B7',
    background: '#D1F9CB',
    text: '#32BF1F',
    label: 'Working',
  },
  late_in: {
    border: '#E7C88C',
    background: '#FFD4A5',
    text: '#D77000',
    label: 'Late In',
  },
  late_out: {
    border: '#E7C88C',
    background: '#FFD4A5',
    text: '#D77000',
    label: 'Late Out',
  },
  early_in: {
    border: '#E7C88C',
    background: '#FFD4A5',
    text: '#D77000',
    label: 'Early In',
  },
  early_out: {
    border: '#E7C88C',
    background: '#FFD4A5',
    text: '#D77000',
    label: 'Early Out',
  },
  not_started: {
    border: '#E7C88C',
    background: '#FFD4A5',
    text: '#D77000',
    label: 'Not Started',
  },
  clocked_out: {
    border: '#C6C6C6',
    background: '#E9E9E9',
    text: '#919191',
    label: 'Clocked Out',
  },
  time_off: {
    border: '#D3D6FA',
    background: '#ECF0FF',
    text: '#6F6DE8',
    label: 'Time Off',
  },
}

const Attendance = ({companyId, placeId, selectedPlace, }) => {
  const { t } = useTranslation()

  const tableBodyRef = useRef(null)
  const clockoutModalRef = useRef(null)
  const contentRef = useRef(null)

  const [currentDate, setCurrentDate] = useState(moment())
  const [{ aggregated, employees }, setData] = useState({
    aggregated: {
      employees_with_planned_time: 0,
      employees_started_work: 0,
      attendance_rate: 0,
      planned_time_duration: 0,
      completed_time_duration: 0,
      employees_who_not_started_work: 0,
      not_started_work_rate: 0,
      working_employees: 0,
      late_arrivals_employees: 0,
      time_off_employees: 0,
    },
    employees: []
  })
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState([])

  const plannedDuration = moment.duration(aggregated.planned_time_duration, 'seconds')
  const completedDuration = moment.duration(aggregated.completed_time_duration, 'seconds')
  const overtimeDuration = moment.duration(Math.max(aggregated.completed_time_duration - aggregated.planned_time_duration, 0), 'seconds')
  const missingDuration = moment.duration(Math.max(aggregated.planned_time_duration - aggregated.completed_time_duration, 0), 'seconds')

  const filteredEmployees = employees.filter(e => {
    if (statusFilter.length && !statusFilter.some(s => e.status.includes(s))) {
      return false
    }
    if (query && !(e.name.toLowerCase().includes(query.toLowerCase()) || e.surname.toLowerCase().includes(query.toLowerCase()))) {
      return false
    }
    return true
  })

  useEffect(() => {
    const rect = contentRef.current.getBoundingClientRect()
    const distance = window.innerHeight - rect.top;
    contentRef.current.style.height = `${distance - 35}px`
  }, [])

  useEffect(() => {
    getAttendance()
  }, [companyId, placeId, currentDate.format('YYYY-MM-DD')])

  const getAttendance = async () => {
    const res = await getPlaceAttendance(companyId, placeId ? placeId : 'all', {date: currentDate.format('YYYY-MM-DD')})
    if (res) {
      setData(res)
    }
  }

  const handleChangeDate = (date) => {
    setCurrentDate(date)
  }

  const handleStopTime = (employees) => {
    clockoutModalRef.current.open(employees)
  }

  const handleStopTimers = () => {
    getAttendance()
  }

  const handleChangeStatusFilter = (status) => () => {
    if (statusFilter.includes(status)) {
      setStatusFilter(statusFilter.filter(s => s !== status))
      return
    }
    setStatusFilter([...statusFilter, status])
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{flex: 1}}>
          <div className={styles.headerTitle}>{placeId ? `${selectedPlace.name}` : t('All places')}</div>
          <div className={styles.headerDesc}>{placeId ? t('Attendance overview') : t('Global attendance overview')}</div>
        </div>
        <div className={styles.headerActions}>
          <DateSelector date={currentDate} onChange={handleChangeDate} />
          <button type="button" disabled={!aggregated.working_employees} className={cn(styles.stopButton, {[styles.active]: aggregated.working_employees})} onClick={() => handleStopTime(employees.filter(e => e.active_timer))}>
            { t('Stop all') }
            {
              aggregated.working_employees
                ? <div className={styles.badge}>
                    { aggregated.working_employees }
                  </div>
                : null
            }
          </button>
        </div>
      </div>
      <div className={styles.content} ref={contentRef}>
        <div className={styles.aggregateContainer}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <RateIcon />
              <div className={styles.cardTitle}>{t('Attendance rate')}</div>
            </div>
            <div className={styles.attendanceRate}>
              {aggregated.attendance_rate}%
            </div>
            <div className={styles.cardFooter}>
              { aggregated.employees_started_work }/{ aggregated.employees_with_planned_time } { t('came to work') }
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <ClockIcon />
              <div className={styles.cardTitle}>{t('Worked vs planned')}</div>
            </div>
            <div className={styles.timeInfo}>
              {Math.floor(completedDuration.asHours())}<span style={{fontWeight: 400, opacity: 0.6}}>h</span> {completedDuration.minutes()}<span style={{fontWeight: 400, opacity: 0.6}}>min</span>
            </div>
            <div className={styles.cardFooter} style={{color: '#000'}}>
              <span style={{opacity: 0.6}}>{ t('Planned') }</span> { Math.floor(plannedDuration.asHours())}<span style={{opacity: 0.6}}>h</span> {plannedDuration.minutes()}<span style={{opacity: 0.6}}>min</span>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <CrossIcon />
              <div className={styles.cardTitle}>{t('No-Shows')}</div>
            </div>
            <div className={styles.noShowInfo}>
              { aggregated.employees_who_not_started_work }
            </div>
            <div className={styles.cardFooter}>
              {aggregated.not_started_work_rate}% { t('absence rate') }
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <CupIcon />
              <div className={styles.cardTitle}>{t('On time off')}</div>
            </div>
            <div className={styles.timeOffInfo}>
              { aggregated.time_off_employees }
            </div>
            <div className={styles.cardFooter}>
              {t('Vacation, sick, parental...')}
            </div>
          </div>
          <div className={cn(styles.card, styles.row)}>
            <div className={styles.cardIconContainer}>
              <ClockIcon color="#808F94" />
            </div>
            <div className={styles.cardFooter}>
              <div>{ t('Currently working') }</div>
              <div className={styles.cardValue}>{ aggregated.working_employees }</div>
            </div>
          </div>
          <div className={cn(styles.card, styles.row)}>
            <div className={styles.cardIconContainer}>
              <AttentionIcon color="#808F94" />
            </div>
            <div className={styles.cardFooter}>
              <div>{ t('Late arrivals') }</div>
              <div className={styles.cardValue}>{ aggregated.late_arrivals_employees }</div>
            </div>
          </div>
          <div className={cn(styles.card, styles.row)}>
            <div className={styles.cardIconContainer}>
              <ArrowIcon color="#808F94" />
            </div>
            <div className={styles.cardFooter}>
              <div>{ t('Overtime') }</div>
              <div className={styles.cardValue}>{ Math.floor(overtimeDuration.asHours()) }<span style={{opacity: 0.6}}>h</span> { overtimeDuration.minutes() }<span style={{opacity: 0.6}}>min</span></div>
            </div>
          </div>
          <div className={cn(styles.card, styles.row)}>
            <div className={styles.cardIconContainer}>
              <ArrowIcon color="#808F94" style={{transform: `rotate(90deg)`}} />
            </div>
            <div className={styles.cardFooter}>
              <div>{ t('Missing hours') }</div>
              <div className={styles.cardValue}>{ Math.floor(missingDuration.asHours()) }<span style={{opacity: 0.6}}>h</span> { missingDuration.minutes() }<span style={{opacity: 0.6}}>min</span></div>
            </div>
          </div>
        </div>
        <div className={styles.tools}>
          <div className={styles.statusFilters}>
            {
              Object.entries(statusMap).map(([key, conf]) => {
                return (
                  <div key={key} className={cn(styles.statusButton, {[styles.active]: statusFilter.includes(key)})} onClick={handleChangeStatusFilter(key)}>
                    { t(conf.label) }: <div className={styles.badge}>{ employees.filter(e => e.status.includes(key)).length }</div>
                  </div>
                )
              })
            }
          </div>
          <input
            type="text"
            placeholder={t('Search by employee name')}
            className={styles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div className={styles.cell}>{ t('Employee') }</div>
            <div className={styles.cell}>{ t('Status') }</div>
            <div className={styles.cell}>{ t('Plan vs fact') }</div>
            <div className={styles.cell}>{ t('Worked') }</div>
            <div className={styles.cell}>{ t('Diff') }</div>
          </div>
          <div className={styles.tableBody} ref={tableBodyRef}>
            {
              filteredEmployees.map((employee) => {
                const diffDuration = moment.duration(employee.diff_duration, 'seconds')
                const workedDuration = moment.duration(employee.worked_duration, 'seconds')
                const previousStartedTimer = employee.active_timer && !moment(employee.active_timer.local_started_at || employee.active_timer.started_at).isSame(currentDate, 'day') ? moment(employee.active_timer.local_started_at) : null

                return (
                  <div key={employee.id} className={styles.tableRow}>
                    <div className={styles.cell}>
                      {
                        employee.avatar
                          ? <img src={employee.avatar} alt={`${employee.name} ${employee.surname}`} className={styles.avatar} />
                          : null
                      }
                      <div style={{display: 'flex', flexDirection: 'column', gap: 4, flex: 1}}>
                        <div>{employee.name} {employee.surname}</div>
                        <div className={styles.skills}>{ employee.skills ? employee.skills.join(', ') : null }</div>
                      </div>
                    </div>
                    <div className={cn(styles.cell, styles.statusCell)}>
                      {
                        employee.status.map((status) => {
                          const statusConf = statusMap[status] || statusMap['not_started']
                          return (
                            <div className={styles.status} style={{borderColor: statusConf.border, backgroundColor: statusConf.background, color: statusConf.text}}>
                              { t(statusConf.label) }
                              {
                                previousStartedTimer && status === 'working'
                                  ? <div
                                      data-tooltip-id='times'
                                      data-tooltip-variant="light"
                                      data-tooltip-content={t('Ongoing from {{date}}', {date: previousStartedTimer.format('MMMM, DD')})}>
                                      <AttentionIcon2 style={{marginBottom: -2}} />
                                    </div>
                                  : null
                              }
                            </div>
                          )
                        })
                      }
                      
                    </div>
                    <div className={styles.cell}>
                      <div>
                        <div className={styles.times}>
                          <div className={styles.tooltipTrigger} data-tooltip-id='times' data-tooltip-content={t('Scheduled time')}>P</div>
                          {employee.planned_time?.start ?? <>&nbsp;&nbsp;&nbsp;...&nbsp;&nbsp;&nbsp;</>}
                          <ArrowIcon style={{transform: 'rotate(45deg)'}} color="#000" />
                          {employee.planned_time?.end ?? <>&nbsp;&nbsp;&nbsp;...&nbsp;&nbsp;&nbsp;</>}
                        </div>
                        <div className={styles.times}>
                          <div className={styles.tooltipTrigger} data-tooltip-id='times' data-tooltip-content={t('Actual time')}>A</div>
                          {
                            employee.active_timer
                              ? <>{employee.active_timer.start} <ArrowIcon style={{transform: 'rotate(45deg)'}} color="#000" /> &nbsp;&nbsp;&nbsp;...&nbsp;&nbsp;&nbsp;</>
                              : <>{employee.completed_time?.start ?? <>&nbsp;&nbsp;&nbsp;...&nbsp;&nbsp;&nbsp;</>} <ArrowIcon style={{transform: 'rotate(45deg)'}} color="#000" /> {employee.completed_time?.end ?? <>&nbsp;&nbsp;&nbsp;...&nbsp;&nbsp;&nbsp;</>}</>
                          }
                        </div>
                      </div>
                    </div>
                    <div className={styles.cell}>
                      {
                        employee.worked_duration && !employee.active_timer
                          ? <div>
                              { workedDuration.hours() ? <>{ workedDuration.hours() } <span style={{opacity: 0.6}}>h</span></> : null } { workedDuration.minutes() }<span style={{opacity: 0.6}}>min</span>
                            </div>
                          : '-'
                      }
                    </div>
                    <div className={styles.cell}>
                      {
                        employee.diff_duration && !employee.active_timer
                          ? <div style={{color: employee.diff_duration > 0 ? '#E62B34' : '#32BF1F'}}>
                              { employee.diff_duration > 0 ? '-' : '+'}{ diffDuration.hours() ? <>{diffDuration.hours() }<span style={{opacity: 0.6}}>h</span></> : null } { Math.abs(diffDuration.minutes()) }<span style={{opacity: 0.6}}>min</span>
                            </div>
                          : '-'
                      }
                    </div>
                    {
                      employee.active_timer
                        ? <button type="button" className={cn(styles.stopButton, styles.active)} onClick={() => handleStopTime([employee])}>
                            { t('Stop time') }
                          </button>
                        : null
                    }
                  </div>
                )
              })
            }
          </div>
        </div>
      </div>
      <Tooltip id="times" className={styles.tooltip} />
      <ClockoutModal
        ref={clockoutModalRef}
        companyId={companyId}
        onStop={handleStopTimers} />
    </div>
  )
}

export default Attendance
